import ConfirmDialog from '@components/Common/ConfirmDialog';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import FilterRow from '@components/Common/FilterRow';
import RecordLink from '@components/Common/RecordLink';
import { useToast } from '@components/Common/Toast/ToastProvider';
import ActionIconButton from '@components/Common/ActionIconButton';
import {
  approveReferralRewardMutation,
  excludeReferralCreditMutation,
  rejectReferralRewardMutation,
  restoreReferralCreditMutation,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { fmtMoney } from '@helpers/money';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import { Box, Button, Checkbox, TablePagination, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import {
  checkLabel,
  type Contributor,
  flagLines,
  hasPerson,
  isScannedCv,
  orderedChecks,
  referrerLabel,
  referrerPath,
  rewardKind,
  rewardTitle,
  type RewardRow,
  VERDICT_TONE,
  verdictLabel,
} from './review';
import { formatDate } from '@helpers/date';

interface ReviewQueueProps {
  rows: RewardRow[];
  total: number;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
  /** Reload after a decision, so the queue and the totals both move. */
  onChanged: () => void;
  /** "Payout details" in one line, from the page, so the two lists cannot disagree. */
  payoutLine: (r: RewardRow) => string | null;
}

type Pending = { kind: 'approve' | 'reject'; rows: RewardRow[] } | null;
/** A milestone contributor being taken out of, or put back into, the count. */
type Exclusion = { contributor: Contributor; restore: boolean } | null;

const sumOf = (rows: RewardRow[]) => fmtMoney(rows.reduce((s, r) => s + (r.amount || 0), 0), rows[0]?.currency ?? 'INR');
const people = (n: number) => (n === 1 ? '1 reward' : `${n} rewards`);

/**
 * Super Admin: rewards waiting for a person to decide. Nothing here is payable until approved.
 *
 * Every row shows its evidence open rather than behind a toggle: the decision is whether money
 * moves, and a reviewer who has to click to see the flags is a reviewer who approves without them.
 */
const ReviewQueue: React.FC<ReviewQueueProps> = ({ rows, total, page, pageSize, onPage, onChanged, payoutLine }) => {
  const { showToast } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const [exclusion, setExclusion] = useState<Exclusion>(null);

  const changeCount = async (reason?: string) => {
    if (!exclusion) return;
    setBusy(true);
    try {
      const id = exclusion.contributor.creditId;
      await fetchGraphQLApiData(exclusion.restore ? restoreReferralCreditMutation(id) : excludeReferralCreditMutation(id, reason ?? ''));
      showToast(exclusion.restore ? 'Counted again' : 'Excluded from milestones', 'success');
      setExclusion(null);
      onChanged();
    } catch (e: any) {
      showToast(String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '') || 'Not saved.', 'error');
    } finally {
      setBusy(false);
    }
  };

  // A selection never survives the rows it was made on: after a reload those ids are gone.
  useEffect(() => setSelected(new Set()), [rows]);

  const chosen = rows.filter((r) => selected.has(r.id));
  const allOnPage = rows.length > 0 && chosen.length === rows.length;
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r.id)));
  const toggleOne = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const decide = async (reason?: string) => {
    if (!pending) return;
    const ids = pending.rows.map((r) => r.id);
    setBusy(true);
    try {
      const r = await fetchGraphQLApiData(
        pending.kind === 'approve' ? approveReferralRewardMutation(ids) : rejectReferralRewardMutation(ids, reason ?? ''),
      );
      const n = Number(r?.data?.[pending.kind === 'approve' ? 'approveReferralReward' : 'rejectReferralReward'] ?? 0);
      showToast(`${pending.kind === 'approve' ? 'Approved' : 'Rejected'} ${people(n)}`, 'success');
      setPending(null);
      onChanged();
    } catch (e: any) {
      showToast(String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '') || 'Not saved.', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (rows.length === 0) {
    return <EmptyState compact title="Nothing to review" body="New qualified referrals appear here before they can be paid." />;
  }

  return (
    <>
      <FilterRow>
        <Box className="rf-review__select-all">
          <Checkbox
            size="small"
            checked={allOnPage}
            indeterminate={!allOnPage && chosen.length > 0}
            onChange={toggleAll}
            slotProps={{ input: { 'aria-label': 'Select every reward on this page' } }}
          />
          <Typography variant="body2">{chosen.length ? `${chosen.length} selected` : 'Select all'}</Typography>
        </Box>
        <Button variant="contained" disabled={!chosen.length} onClick={() => setPending({ kind: 'approve', rows: chosen })}>
          Approve selected
        </Button>
        <Button variant="outlined" color="error" disabled={!chosen.length} onClick={() => setPending({ kind: 'reject', rows: chosen })}>
          Reject selected
        </Button>
      </FilterRow>

      <Box component="ul" className="rf-review-list">
        {rows.map((r) => {
          const checks = orderedChecks(r.checks);
          const flags = flagLines(r.flags);
          const rel = r.relevance;
          const verdict = rel?.verdict ?? '';
          const kind = rewardKind(r);
          const who = rewardTitle(r);
          const referrer = (
            <>
              <RecordLink to={referrerPath(r)}>{r.referrerName || r.referrerEmail || r.referrerId}</RecordLink>
              {` (${referrerLabel(r.referrerType)})`}
            </>
          );
          return (
            <Box component="li" key={r.id} className="rf-review" data-testid="rf-review-row" data-kind={kind}>
              <Box className="rf-review__head">
                <Checkbox
                  size="small"
                  checked={selected.has(r.id)}
                  onChange={() => toggleOne(r.id)}
                  slotProps={{ input: { 'aria-label': `Select ${who}` } }}
                />
                <Box className="rf-review__who">
                  {hasPerson(r) ? (
                    <Typography variant="body2" component="div">
                      {kind === 'PLACEMENT' && 'Placement: '}
                      <RecordLink to={`/jobseekers/${r.referredId}`}>{r.referredName || r.referredId}</RecordLink>
                      {kind === 'PLACEMENT' ? ' was hired · ' : ' · '}
                      <RecordLink to={`/jobseekers/${r.referredId}/documents`}>CV</RecordLink>
                    </Typography>
                  ) : (
                    <Typography variant="body2" component="div">{who}</Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" component="div">
                    {kind === 'DRAW' ? 'Won by ' : kind === 'TIER' ? 'Reached by ' : 'Referred by '}
                    {referrer}
                    {r.earnedAt ? `, ${kind === 'PER_PERSON' ? 'qualified' : 'on'} ${formatDate(r.earnedAt)}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" component="div">
                    {payoutLine(r) ?? 'No payout details given'}
                  </Typography>
                </Box>
                <Typography variant="subtitle2" className="rf-review__amount">{fmtMoney(r.amount, r.currency)}</Typography>
                <Box className="rf-review__actions">
                  <Button size="small" variant="contained" onClick={() => setPending({ kind: 'approve', rows: [r] })}>
                    Approve
                  </Button>
                  <Button size="small" variant="outlined" color="error" onClick={() => setPending({ kind: 'reject', rows: [r] })}>
                    Reject
                  </Button>
                </Box>
              </Box>

              {kind === 'TIER' && (
                <Box className="rf-review__block">
                  <Typography variant="caption" color="text.secondary" component="div">Milestone</Typography>
                  <Typography variant="body2" component="div">
                    {[
                      `Pays ${fmtMoney(r.tierAmount ?? r.amount, r.currency)} in full`,
                      (r.legacyNetted ?? 0) > 0
                        ? `${fmtMoney(r.legacyNetted ?? 0, r.currency)} already earned per friend is netted off`
                        : null,
                    ]
                      .filter(Boolean)
                      .join('. ')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {`Counted (${r.contributors?.length ?? 0})`}
                  </Typography>
                  <Box className="rf-chips">
                    {(r.contributors ?? []).map((c) => (
                      <Box key={c.creditId} className="rf-contributor">
                        <CRMStatusChip status={c.excluded ? 'inactive' : 'success'} label={`${c.name || 'Unknown'}${c.excluded ? ', excluded' : ''}`} />
                        <ActionIconButton
                          size="small"
                          label={c.excluded ? `Count ${c.name || 'this person'} again` : `Exclude ${c.name || 'this person'} from milestones`}
                          icon={c.excluded ? <UndoOutlinedIcon fontSize="small" /> : <PersonOffOutlinedIcon fontSize="small" />}
                          onClick={() => setExclusion({ contributor: c, restore: c.excluded })}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {kind === 'DRAW' && (
                <Box className="rf-review__block">
                  <Typography variant="body2" color="text.secondary" component="div">
                    The winner was drawn at random from this month&apos;s entries. The Draws tab shows the entries and how the
                    winner can be checked.
                  </Typography>
                </Box>
              )}

              {hasPerson(r) && (
                <>
              <Box className="rf-review__block">
                <Typography variant="caption" color="text.secondary" component="div">Checks</Typography>
                <Box className="rf-chips">
                  {checks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No checks recorded</Typography>
                  ) : (
                    checks.map((c) => (
                      <CRMStatusChip
                        key={c.name}
                        status={c.passed ? 'success' : 'failed'}
                        label={`${c.passed ? 'Yes' : 'No'}: ${checkLabel(c.name)}`}
                      />
                    ))
                  )}
                </Box>
              </Box>

              <Box className="rf-review__block">
                <Typography variant="caption" color="text.secondary" component="div">Industry evidence</Typography>
                <Box className="rf-chips">
                  <CRMStatusChip status={VERDICT_TONE[verdict] ?? 'pending'} label={verdictLabel(verdict)} />
                  {isScannedCv(r.checks, rel) && <CRMStatusChip status="needsreview" label="Scanned CV: check by eye" />}
                </Box>
                <Typography variant="body2" component="div">
                  {`Matched: ${rel?.matchedTerms?.length ? rel.matchedTerms.join(', ') : 'none'}`}
                </Typography>
                {!!rel?.negativeTerms?.length && (
                  <Typography variant="body2" component="div">{`Other fields: ${rel.negativeTerms.join(', ')}`}</Typography>
                )}
                <Typography variant="body2" color="text.secondary" component="div">
                  {[
                    `CV text: ${(rel?.cvTextChars ?? r.resumeChars ?? 0).toLocaleString('en-IN')} characters`,
                    typeof r.completenessAtQualification === 'number' ? `profile ${r.completenessAtQualification}% complete` : null,
                    rel?.industries?.length ? `industries ${rel.industries.join(', ')}` : null,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              </Box>

              <Box className="rf-review__block">
                <Typography variant="caption" color="text.secondary" component="div">Flags</Typography>
                <Box className="rf-chips">
                  {flags.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Nothing unusual</Typography>
                  ) : (
                    flags.map((f) => (
                      <CRMStatusChip key={f.text} status={f.warn ? 'needsreview' : 'inactive'} label={f.text} />
                    ))
                  )}
                </Box>
              </Box>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {total > pageSize && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
          onPageChange={(_e, n) => onPage(n)}
        />
      )}

      <ConfirmDialog
        open={pending?.kind === 'approve'}
        title={`Approve ${people(pending?.rows.length ?? 0)}?`}
        body={
          pending
            ? `${sumOf(pending.rows)} becomes payable and moves to Approved, to pay. Referrers of a friend's reward are emailed that it was approved.`
            : ''
        }
        actionLabel="Approve"
        actionColor="primary"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => decide()}
      />
      <ConfirmDialog
        open={!!exclusion && !exclusion.restore}
        title={`Exclude ${exclusion?.contributor.name || 'this person'} from milestones?`}
        body="They stop counting toward this referrer's milestones. A milestone already reached keeps its amount: reject it as well if it should not be paid. Their credit stays, so they cannot be referred again."
        reasonLabel="Reason (kept for the audit)"
        reasonPlaceholder="For example: same household as the referrer"
        actionLabel="Exclude"
        busy={busy}
        onCancel={() => setExclusion(null)}
        onConfirm={(reason) => changeCount(reason)}
      />
      <ConfirmDialog
        open={!!exclusion && exclusion.restore}
        title={`Count ${exclusion?.contributor.name || 'this person'} again?`}
        body="They count toward this referrer's milestones again, and any milestone that now reaches its number is created for review."
        actionLabel="Count again"
        actionColor="primary"
        busy={busy}
        onCancel={() => setExclusion(null)}
        onConfirm={() => changeCount()}
      />
      <ConfirmDialog
        open={pending?.kind === 'reject'}
        title={`Reject ${people(pending?.rows.length ?? 0)}?`}
        body={pending ? `${sumOf(pending.rows)} will not be paid. The referrer sees the reason you give.` : ''}
        reasonLabel="Reason (shown to the referrer)"
        reasonPlaceholder="For example: the CV shows no jewellery or retail experience"
        actionLabel="Reject"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={(reason) => decide(reason)}
      />
    </>
  );
};

export default ReviewQueue;
